import React, { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import UserInfo from "@utils/UserInfo";
import { useNavigate, useParams } from "react-router-dom";
import { handleApiError } from "@utils/errorHandler";
import ClipLoader from "react-spinners/ClipLoader";

const ReportingMenu = ({ isOpen, handleNavItemClick, handleContextMenu }) => {
  const userInfo = UserInfo.getUser();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    if (!isOpen) {
      setHasFetched(false);
      setDashboards([]);
      return;
    }

    if (hasFetched) {
      return;
    }

    let cancelled = false;

    const fetchDashboards = async () => {
      setLoading(true);

      try {
        const response = await axios.get("/general/reportingDashboards", {
          params: {
            adminId: userInfo?.id,
          },
        });

        if (cancelled) return;

        const fetchedDashboards = response.data || [];

        setDashboards(fetchedDashboards);
        setHasFetched(true);

        localStorage.setItem(
          "reportingDashboards",
          JSON.stringify(
            fetchedDashboards.map(({ uuid, urlSlug, title }) => ({
              uuid,
              urlSlug,
              title,
            }))
          )
        );

        if (fetchedDashboards.length === 0) {
          navigate("/404", { replace: true });
          return;
        }

        // Keep the current submenu if the user has access to it.
        const currentDashboard = fetchedDashboards.find(
          (dashboard) => dashboard.urlSlug === params.path
        );

        // Navigate to the first submenu only if the current one is inaccessible.
        if (!currentDashboard) {
          navigate(`/report/${fetchedDashboards[0].urlSlug}`, {
            replace: true,
          });
        }
      } catch (error) {
        if (!cancelled) {
          handleApiError(error);
          setHasFetched(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDashboards();

    return () => {
      cancelled = true;
    };
  }, [isOpen, hasFetched, navigate, params.path, userInfo?.id]);
  
  if (!isOpen) {
    return null;
  }

  return (
    <div className={dashboards?.length > 0 ? "sub-list" : ""}>
      <ul>
        {loading && (
          <li className="reporting-submenu-item">
            <ClipLoader size={15} color="#fff" />
          </li>
        )}

        {!loading &&
          dashboards?.map((dashboard) => {
            const path = `/report/${dashboard.urlSlug}`;

            return (
              <li
                key={dashboard.uuid}
                data-link={path}
                onClick={handleNavItemClick}
                onContextMenu={handleContextMenu}
                className={params.path === dashboard.urlSlug ? "active" : ""}
              >
                {dashboard.title}
              </li>
            );
          })}
      </ul>
    </div>
  );
};

ReportingMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleNavItemClick: PropTypes.func.isRequired,
  handleContextMenu: PropTypes.func.isRequired,
};

export default ReportingMenu;
